/**
 * Evaluate every threshold on a rule against a facts bag — for live UI verdicts.
 * (Engine evaluates the same predicates; this mirrors them for display.)
 */
import {
  type EvaluatedElement,
  ontologyPropertyToFactKey,
  type RulePredicate,
  type RuleResult,
  type RuleSpec,
} from "@vera/rules";

export type PredicateCheckStatus = "pass" | "fail" | "missing";

export interface PredicateCheck {
  factKey: string;
  label: string;
  observed?: number;
  threshold: number;
  unit: string;
  kind: RulePredicate["kind"];
  status: PredicateCheckStatus;
}

const PREDICATE_LABELS: Record<string, string> = {
  glazingAreaRatio: "Glazing area",
  openableAreaRatio: "Openable area",
};

export function allRulePredicates(rule: RuleSpec): RulePredicate[] {
  return [rule.predicate, ...(rule.conjunctivePredicates ?? [])];
}

function quantityFromFacts(
  facts: EvaluatedElement["facts"],
  factKey: string
): number | undefined {
  const v = facts[factKey];
  if (v && typeof v === "object" && "value" in v && "unit" in v) {
    const n = (v as { value: number }).value;
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function checkStatus(
  kind: RulePredicate["kind"],
  observed: number,
  threshold: number
): PredicateCheckStatus {
  if (kind === "minimum") return observed < threshold ? "fail" : "pass";
  if (kind === "maximum") return observed > threshold ? "fail" : "pass";
  return observed === threshold ? "pass" : "fail";
}

export function evaluatePredicateChecks(
  rule: RuleSpec,
  facts: EvaluatedElement["facts"]
): PredicateCheck[] {
  return allRulePredicates(rule).map(pred => {
    const factKey = ontologyPropertyToFactKey(pred.property);
    const observed = quantityFromFacts(facts, factKey);
    const label =
      PREDICATE_LABELS[factKey] ??
      factKey.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase());

    const threshold = pred.threshold ?? 0;
    if (observed === undefined) {
      return {
        factKey,
        label,
        threshold,
        unit: pred.unit,
        kind: pred.kind,
        status: "missing",
      };
    }

    return {
      factKey,
      label,
      observed,
      threshold,
      unit: pred.unit,
      kind: pred.kind,
      status: checkStatus(pred.kind, observed, threshold),
    };
  });
}

export type VerdictTone = "fail" | "pass" | "review" | "unknown";

export interface RuleVerdict {
  tone: VerdictTone;
  headline: string;
  detail: string;
}

export function ruleVerdictFromChecks(
  checks: PredicateCheck[],
  engineResult: RuleResult | null
): RuleVerdict {
  const failing = checks.filter(c => c.status === "fail");
  const missing = checks.filter(c => c.status === "missing");

  if (failing.length > 0) {
    return {
      tone: "fail",
      headline: "Does not comply",
      detail: failing.map(c => formatCheckLine(c)).join("\n"),
    };
  }

  if (missing.length > 0) {
    return {
      tone: "unknown",
      headline: "Needs more information",
      detail: `Missing: ${missing.map(c => c.label).join(", ")}`,
    };
  }

  if (
    engineResult?.status === "compliant" ||
    engineResult?.status === "compliant_via_exception"
  ) {
    return {
      tone: "pass",
      headline: "Complies",
      detail: checks.map(c => formatCheckLine(c)).join("\n"),
    };
  }

  if (engineResult?.status === "confirmed_violation") {
    return {
      tone: "fail",
      headline: "Does not comply",
      detail: engineResult.summary,
    };
  }

  return {
    tone: "review",
    headline: "Measurements pass — not a confirmed violation",
    detail:
      `${checks.map(c => formatCheckLine(c)).join("\n")}\n` +
      "Optional facts still needed to rule out code exceptions.",
  };
}

export function formatCheckLine(c: PredicateCheck): string {
  const limitWord =
    c.kind === "minimum" ? "min" : c.kind === "maximum" ? "max" : "req";
  const unit = c.unit === "unitless" ? "%" : ` ${c.unit}`;
  if (c.status === "missing") {
    return `${c.label}: not provided · ${limitWord} ${c.threshold}${unit}`;
  }
  const mark = c.status === "pass" ? "✓" : "✗";
  return `${c.label}: ${c.observed}${unit} · ${limitWord} ${c.threshold}${unit} ${mark}`;
}

export function overlayChipText(
  sectionRef: string,
  verdict: RuleVerdict
): string {
  return `${sectionRef} — ${verdict.headline}\n${verdict.detail}`;
}
